import type { PkceTokenRequest, PkceTokenResponse } from '@fleet-bffs/api-contract';
import { bffWebApi } from '~/api';
import type { CurrentUserInfo } from '~/ducks/authentication/data/AuthenticationState';

const oidcAuthApi = bffWebApi.injectEndpoints({
  endpoints: (build) => ({
    exchangeToken: build.mutation<PkceTokenResponse, PkceTokenRequest>({
      query: (body) => ({
        url: `${process.env.ENDPOINT_BFF}/token`,
        method: 'POST',
        body,
      }),
    }),
    logout: build.mutation<void, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/token`,
        method: 'DELETE',
      }),
    }),
    checkCurrentUser: build.mutation<CurrentUserInfo, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/users/current`,
        method: 'GET',
        // Right after login — never trust a response cached for a previous session.
        cache: 'no-cache',
      }),
    }),
    fetchCurrentUser: build.query<CurrentUserInfo, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/users/current`,
        method: 'GET',
      }),
    }),
    /**
     * Same request as `fetchCurrentUser`, but bypasses the service worker cache (it serves
     * `/users/current` stale-while-revalidate). Use it whenever the result must reflect the
     * server's current state, e.g. the post-launch refresh of the user.
     */
    refreshCurrentUser: build.query<CurrentUserInfo, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/users/current`,
        method: 'GET',
        cache: 'no-cache',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Keep fetchCurrentUser in sync, so a later read of it doesn't bring back the stale user.
          dispatch(oidcAuthApi.util.upsertQueryData('fetchCurrentUser', undefined, data));
        } catch {
          // A failed refresh leaves fetchCurrentUser untouched.
        }
      },
    }),
  }),
});

export const {
  useExchangeTokenMutation,
  useLogoutMutation,
  useCheckCurrentUserMutation,
  useFetchCurrentUserQuery,
} = oidcAuthApi;
export default oidcAuthApi;
